import React, { useState, useEffect, useRef } from 'react'

import SimpleBar from 'simplebar-react'
import { useSelector, useDispatch } from 'react-redux'
import { validateNumber, sipNumberFormat, numberValidationError } from 'utils/numberUtils'
import ButtonsList from './buttonsList'
import { Contacts } from 'components/pages'
import { ContactsPageTypes, CallPageTypes } from 'common/Enums'
import {
    setTransferModalVisible,
    setContactsModalVisible,
    requestContactStart,
    requestContactFinish
} from 'store/actions/global'
import { ModalWithFullSize, PageHeaderWithButtons } from 'components/shared'
import PhoneInput from 'components/pages/dialpad/components/PhoneInput'
import {
    changePhoneNumber,
    transfer,
    addNewCall,
    setCallTransfer,
    validationPhoneError
} from 'store/actions/call'
import Webphone from 'common/Webphone'
import { useDebounce } from 'use-debounce'

import './styles.scss'
import { useTranslation } from 'react-i18next'
import { navigateCallPage } from 'hooks'

export function TransferCallContactsModal() {
    const [inputValue, setInputValue] = useState('')
    const [isDisabled, setIsDisabled] = useState(true)
    const [isRequest, setIsRequest] = useState(true)
    const [contactClickFromListHandler, setContactClickFromListHandler] = useState(true)
    const [debouncedValue] = useDebounce(inputValue, 1200)

    const scrollContainerRef = useRef(null)
    const dispatch = useDispatch()

    const transferModalVisible = useSelector((store) => store.global.transferModalVisible)
    const addNumberModalVisible = useSelector((store) => store.global.addNumberModalVisible)
    const { calls } = useSelector((state) => state.call)
    const { transferNumber } = useSelector((state) => state.call)
    const currentCall = calls.find((item) => item.isActive)

    //request contact start/finish
    useEffect(() => {
        if (inputValue.length) {
            const currentCallNumber = calls.filter((item) => item.number === inputValue)
            if (!currentCallNumber.length && contactClickFromListHandler && isRequest) {
                dispatch(requestContactStart())
            } else {
                dispatch(requestContactFinish())
            }
        }
    }, [dispatch, inputValue, calls, contactClickFromListHandler, isRequest])

    //disable/enable buttons
    useEffect(() => {
        if (inputValue.length) {
            setIsDisabled(currentCall?.number === inputValue)
        } else {
            setIsDisabled(true)
        }
    }, [inputValue, currentCall])

    const fillInputValue = (value) => setInputValue(value || '')
    const addInputValue = (value) => {
        setInputValue((prevState) => `${prevState}${value}`)
        setIsRequest(false)
    }
    const resetInputValue = () => {
        setInputValue('')
        dispatch(requestContactFinish())
    }
    const onBackspace = () => {
        setInputValue((prevState) => prevState.slice(0, -1))
        setIsRequest(false)
    }
    function setContactToInput(value) {
        setInputValue(value)
        setContactClickFromListHandler(false)
    }

    const onChangeTarget = (value) => {
        if (value === 'input') {
            setContactClickFromListHandler(true)
            setIsRequest(true)
        }
    }

    function closeModal() {
        transferModalVisible
            ? transferModalVisible && dispatch(setTransferModalVisible(false))
            : addNumberModalVisible && dispatch(setContactsModalVisible(false))
    }

    function onModalHandler() {
        setInputValue('')
        closeModal()
    }

    //dispacthTransfer
    const addTransferActionsDispatch = (inputValue) => {
        const transferingNumber = currentCall.isConference ? transferNumber : currentCall.number
        const transferToNumber = sipNumberFormat(inputValue)
        
        dispatch(
            transfer({
                transferingNumber,
                transferToNumber
            })
        )

        closeModal()
    }

    function transferCallHandler() {
        validateNumber(inputValue)
            ? addTransferActionsDispatch(inputValue)
            : dispatch(validationPhoneError(numberValidationError(inputValue)))
    }

    const addCallEvents = () => {
        if (transferModalVisible) {
            dispatch(setCallTransfer({ isOnTransfer: true, transferNumber: currentCall.number }))
        }

        closeModal()

        setInputValue('')
        navigateCallPage({ callPagetype: CallPageTypes.Multi })
    }

    //dispatchAdd
    const addCallActionsDispatch = (inputValue) => {
        const sipInputValue = sipNumberFormat(inputValue)
        const isTalkFirst = transferModalVisible && currentCall?.number
        const existingCallId = isTalkFirst ? Webphone.getCallIdForNumber(currentCall.number) : null
        dispatch(
            changePhoneNumber({
                activeNumber: currentCall?.number,
                number: sipInputValue,
                ...(isTalkFirst && { existingCallId })
            })
        )
        dispatch(addNewCall({ number: sipInputValue, activeCalls: calls.map((item) => item.number) }))
        addCallEvents()
    }

    function addNewCallHandler() {
        if (transferModalVisible && calls.length > 0) {
            const sipInputValue = sipNumberFormat(inputValue)
            const isHoldNumberCalls = calls.filter(
                (item) => item.isHold === true && item.number === sipInputValue
            )

            if (isHoldNumberCalls.length > 0) {
                Webphone.swap(currentCall.number)
                Webphone.setHold(sipInputValue, false)
                addCallEvents()
                return
            }
        }

        validateNumber(inputValue)
            ? addCallActionsDispatch(inputValue)
            : dispatch(validationPhoneError(numberValidationError(inputValue)))
    }

    const { t } = useTranslation()

    const buttonsConfigTransfer = [
        {
            classes: 'webphone-modal__action-btn webphone-modal__action-btn-outline',
            text: t('talk_first'),
            status: isDisabled,
            callHandler: addNewCallHandler
        },
        {
            classes: 'webphone-modal__action-btn',
            text: t('transfer_now'),
            status: isDisabled,
            callHandler: transferCallHandler
        }
    ]

    const buttonsConfigCall = [
        {
            classes: 'webphone-modal__action-btn',
            text: t('call_now'),
            status: isDisabled,
            callHandler: addNewCallHandler
        }
    ]

    const buttonsConfig = transferModalVisible ? buttonsConfigTransfer : buttonsConfigCall
    const slug = transferModalVisible ? ContactsPageTypes.TransferCall : ContactsPageTypes.AddContact
    const pageName = transferModalVisible ? t('transfer_to') : t('add_to')
    //transferModalVisible || addNumberModalVisible
    return (
        <ModalWithFullSize isModalVisible={transferModalVisible || addNumberModalVisible}>
            <SimpleBar ref={scrollContainerRef} className="app__container simplebar-custom">
                <PageHeaderWithButtons pageName={pageName} backHandler={() => {}} onHideModal={onModalHandler} />
                <PhoneInput
                    value={inputValue}
                    onChange={fillInputValue}
                    onTarget={onChangeTarget}
                    onReset={resetInputValue}
                />
                <Contacts
                    modalSlug={slug}
                    inputHandler={false}
                    inputExternalValue={debouncedValue}
                    inputValueProp={inputValue}
                    backHandler={closeModal}
                    scrollContainerRef={scrollContainerRef}
                    onContactClick={setContactToInput}
                    isHeaderVisible={false}
                    contactClickFromListHandler={contactClickFromListHandler}
                    isPhoneKeypadForNoContacts={true}
                    addInputValue={addInputValue}
                    onBackspace={onBackspace}
                    isRequest={isRequest}
                />
                <ButtonsList buttonsConfig={buttonsConfig} />
            </SimpleBar>
        </ModalWithFullSize>
    )
}
